// admin/AdminDashboard.jsx
import React, { useState, useEffect } from 'react';
import {
  ChartBarIcon,
  UserGroupIcon,
  BriefcaseIcon,
  EnvelopeIcon,
  CalendarDaysIcon,
  TrashIcon,
  EyeIcon,
  PencilIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  XMarkIcon,
  PlusIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const generateSignature = (apiSecret, method, path, timestamp) => {
  const payload = timestamp + method + path; // Usando Web Crypto API para generar HMAC-SHA256

  const encoder = new TextEncoder();
  const key = encoder.encode(apiSecret);
  const message = encoder.encode(payload);

  return crypto.subtle
    .importKey('raw', key, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign'])
    .then((cryptoKey) => {
      return crypto.subtle.sign('HMAC', cryptoKey, message);
    })
    .then((signature) => {
      // Convertir ArrayBuffer a hex string
      return Array.from(new Uint8Array(signature))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
    });
};

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    total_postulaciones: 0,
    vacantes_activas: 0,
    postulaciones_hoy: 0,
    postulaciones_este_mes: 0,
    areas_populares: [],
    vacantes_populares: [],
    ultimas_postulaciones: [],
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [postulaciones, setPostulaciones] = useState([]);
  const [vacantes, setVacantes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedArea, setSelectedArea] = useState('');
  const [selectedVacante, setSelectedVacante] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState(null);

  // Estados para modales de confirmación y error
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalData, setConfirmModalData] = useState({
    title: '',
    message: '',
    onConfirm: null,
    postId: null,
  });
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');

  // Estados para nueva/edición de vacante
  const [showVacanteModal, setShowVacanteModal] = useState(false);
  const [editingVacante, setEditingVacante] = useState(null);
  const [vacanteForm, setVacanteForm] = useState({
    status: 'activo',
    idioma_vacante: 'Español',
    slug: '',
    titulo_vacante: '',
    descripcion_corta: '',
    grado_estudios: '',
    responsabilidades: '',
    area_departamento: '',
    nombre_empresa: '',
    calle: '',
    localidad: '',
    estado: '',
    codigo_postal: '',
    pais: 'MX',
    moneda: 'MXN',
    salario: '',
    tipo_salario: 'Mensual',
    perfil: '',
    actividades: '',
    ofrecemos: '',
    lugar_trabajo: '',
    msj_importante: '',
    fecha_posteo: new Date().toISOString().split('T')[0],
    tipo_empleo: 'Tiempo completo',
    horario: '',
    info_empresa: '',
    web_empresa: '',
    correo_empresa: '',
    finaliza_vacante: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split('T')[0],
    url_ubicacion: '',
  });

  // Estados para filtros de vacantes - Inicializar filtroStatus con 'activo'
  const [filtroStatus, setFiltroStatus] = useState('activo'); // Cambiado a 'activo' por defecto
  const [filtroArea, setFiltroArea] = useState('');
  const [filtroOrden, setFiltroOrden] = useState('');

  const generateSlug = (text) => {
    if (!text) return '';
    return text
      .toLowerCase()
      .normalize('NFD') // Separar acentos
      .replace(/[\u0300-\u036f]/g, '') // Eliminar acentos
      .replace(/[^a-z0-9]+/g, '-') // Reemplazar caracteres no alfanuméricos por guiones
      .replace(/^-+|-+$/g, '') // Eliminar guiones al inicio y final
      .trim();
  };

  useEffect(() => {
    // Solo generar slug automáticamente si estamos creando una nueva vacante
    // o si estamos editando y el título ha cambiado
    if (showVacanteModal && vacanteForm.titulo_vacante) {
      const newSlug = generateSlug(vacanteForm.titulo_vacante);
      setVacanteForm((prev) => ({
        ...prev,
        slug: newSlug,
      }));
    }
  }, [vacanteForm.titulo_vacante, showVacanteModal]);

  useEffect(() => {
    fetchStats();
    fetchPostulaciones();
    fetchVacantes();
  }, []);

  useEffect(() => {
    if (activeTab === 'postulaciones') {
      const timer = setTimeout(() => {
        handleSearch();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [searchTerm, selectedArea, selectedVacante]);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');

      const timestamp = Math.floor(Date.now() / 1000).toString();

      const signatureOneApplicationForm = await generateSignature(
        process.env.REACT_APP_API_SECRET, // Necesitas agregar esto en tus variables de entorno
        'GET', // Ejemplo: 'GET'
        `/admin/estadisticas`, // Ejemplo: '/categories/with_counts/'
        timestamp
      );

      const response = await fetch(`${API_BASE_URL}/admin/estadisticas`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Timestamp': timestamp,
          'X-Signature': signatureOneApplicationForm,
          'X-API-Key': process.env.REACT_APP_API_KEY,
        },
      });
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchPostulaciones = async (params = {}) => {
    try {
      const token = localStorage.getItem('admin_token');
      const queryParams = new URLSearchParams(params).toString();

      const timestamp = Math.floor(Date.now() / 1000).toString();

      const signatureTwoApplicationForm = await generateSignature(
        process.env.REACT_APP_API_SECRET, // Necesitas agregar esto en tus variables de entorno
        'GET', // Ejemplo: 'GET'
        `/admin/postulaciones`, // Ejemplo: '/categories/with_counts/'
        timestamp
      );

      const response = await fetch(
        `${API_BASE_URL}/admin/postulaciones${
          queryParams ? '?' + queryParams : ''
        }`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Timestamp': timestamp,
            'X-Signature': signatureTwoApplicationForm,
            'X-API-Key': process.env.REACT_APP_API_KEY,
          },
        }
      );
      const data = await response.json();
      setPostulaciones(data);
    } catch (error) {
      console.error('Error fetching postulaciones:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVacantes = async () => {
    try {
      const token = localStorage.getItem('admin_token');

      const timestamp = Math.floor(Date.now() / 1000).toString();

      const signatureThreeApplicationForm = await generateSignature(
        process.env.REACT_APP_API_SECRET, // Necesitas agregar esto en tus variables de entorno
        'GET', // Ejemplo: 'GET'
        `/vacantes/`, // Ejemplo: '/categories/with_counts/'
        timestamp
      );

      const response = await fetch(`${API_BASE_URL}/vacantes/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Timestamp': timestamp,
          'X-Signature': signatureThreeApplicationForm,
          'X-API-Key': process.env.REACT_APP_API_KEY,
        },
      });

      if (!response.ok) {
        throw new Error('Error al cargar vacantes');
      }

      const data = await response.json();
      setVacantes(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching vacantes:', error);
      setVacantes([]);
    }
  };

  const handleSearch = () => {
    const params = {};
    if (searchTerm) params.nombre = searchTerm;
    if (selectedArea) params.area_interes = selectedArea;
    if (selectedVacante) params.vacante_id = selectedVacante;

    fetchPostulaciones(params);
  };

  const handleExport = async (format) => {
    try {
      const token = localStorage.getItem('admin_token');

      const timestamp = Math.floor(Date.now() / 1000).toString();

      const signatureFourApplicationForm = await generateSignature(
        process.env.REACT_APP_API_SECRET, // Necesitas agregar esto en tus variables de entorno
        'GET', // Ejemplo: 'GET'
        `/admin/postulaciones/exportar`, // Ejemplo: '/categories/with_counts/'
        timestamp
      );

      const response = await fetch(
        `${API_BASE_URL}/admin/postulaciones/exportar?formato=${format}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Timestamp': timestamp,
            'X-Signature': signatureFourApplicationForm,
            'X-API-Key': process.env.REACT_APP_API_KEY,
          },
        }
      );
      const data = await response.json();

      if (format === 'csv') {
        const blob = new Blob([data.content], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else if (format === 'json') {
        const blob = new Blob([JSON.stringify(data.data, null, 2)], {
          type: 'application/json',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error exporting data:', error);
      showNotification('Error al exportar los datos', 'error');
    }
  };

  // Función para mostrar modal de confirmación de eliminación
  const showDeleteConfirmationModal = (postulacionId) => {
    setConfirmModalData({
      title: '¿Estás seguro de eliminar esta postulación?',
      message:
        'Esta acción no se puede deshacer. La postulación será eliminada permanentemente.',
      onConfirm: () => handleDeletePostulacion(postulacionId),
      postId: postulacionId,
    });
    setShowConfirmModal(true);
  };

  // Función para mostrar modal de confirmación de eliminación
  const showDeleteVacanteConfirmationModal = (vacanteId) => {
    setConfirmModalData({
      title: '¿Estás seguro de eliminar esta vacante?',
      message:
        'Esta acción no se puede deshacer. La vacante será eliminada permanentemente.',
      onConfirm: () => handleDeleteVacante(vacanteId),
      postId: vacanteId,
    });
    setShowConfirmModal(true);
  };

  const handleDeletePostulacion = async (id) => {
    try {
      const token = localStorage.getItem('admin_token');

      const timestamp = Math.floor(Date.now() / 1000).toString();

      const signatureFiveApplicationForm = await generateSignature(
        process.env.REACT_APP_API_SECRET, // Necesitas agregar esto en tus variables de entorno
        'DELETE', // Ejemplo: 'GET'
        `/admin/postulaciones/${id}`, // Ejemplo: '/categories/with_counts/'
        timestamp
      );

      const response = await fetch(
        `${API_BASE_URL}/admin/postulaciones/${id}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Timestamp': timestamp,
            'X-Signature': signatureFiveApplicationForm,
            'X-API-Key': process.env.REACT_APP_API_KEY,
          },
        }
      );

      if (response.ok) {
        fetchPostulaciones();
        fetchStats();
        showNotification('Postulación eliminada correctamente', 'success');
      } else {
        throw new Error('Error al eliminar la postulación');
      }
    } catch (error) {
      console.error('Error deleting postulación:', error);
      showErrorModalMessage('Error al eliminar la postulación');
    }
  };

  const handleDeleteVacante = async (id) => {
    try {
      const token = localStorage.getItem('admin_token');

      const timestamp = Math.floor(Date.now() / 1000).toString();

      const signatureSixApplicationForm = await generateSignature(
        process.env.REACT_APP_API_SECRET, // Necesitas agregar esto en tus variables de entorno
        'DELETE', // Ejemplo: 'GET'
        `/admin/vacantes/${id}`, // Ejemplo: '/categories/with_counts/'
        timestamp
      );

      const response = await fetch(`${API_BASE_URL}/admin/vacantes/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Timestamp': timestamp,
          'X-Signature': signatureSixApplicationForm,
          'X-API-Key': process.env.REACT_APP_API_KEY,
        },
      });

      if (response.ok) {
        fetchVacantes();
        fetchStats();
        showNotification('Vacante eliminada correctamente', 'success');
      } else {
        throw new Error('Error al eliminar la vacante');
      }
    } catch (error) {
      console.error('Error deleting vacante:', error);
      showNotification('Error al eliminar la vacante', 'error');
    }
  };

  // Función para mostrar mensaje de error en modal
  const showErrorModalMessage = (message) => {
    setErrorModalMessage(message);
    setShowErrorModal(true);
  };

  // Función para abrir PDF en modal
  const abrirPDFModal = async (postulacionId, nombreArchivo) => {
    try {
      const token = localStorage.getItem('admin_token');
      const timestamp = Math.floor(Date.now() / 1000).toString();

      // Primero verificamos si tiene adjunto
      const signatureInfo = await generateSignature(
        process.env.REACT_APP_API_SECRET,
        'GET',
        `/admin/postulaciones/${postulacionId}/adjunto/info`,
        timestamp
      );

      const infoResponse = await fetch(
        `${API_BASE_URL}/admin/postulaciones/${postulacionId}/adjunto/info`,
        {
          headers: {
            'X-Timestamp': timestamp,
            'X-Signature': signatureInfo,
            'X-API-Key': process.env.REACT_APP_API_KEY,
          },
        }
      );

      if (!infoResponse.ok) {
        throw new Error('Error al verificar adjunto');
      }

      const data = await infoResponse.json();

      if (data.tiene_adjunto) {
        // Generar nueva firma para ver el PDF
        const signatureView = await generateSignature(
          process.env.REACT_APP_API_SECRET,
          'GET',
          `/admin/postulaciones/${postulacionId}/ver-adjunto`,
          timestamp
        );

        // IMPORTANTE: Los headers deben ir en los headers de la petición, no en la URL
        // Para mostrar en iframe, necesitamos una URL que incluya los headers
        // Una solución es crear un blob URL con los headers incluidos

        const viewResponse = await fetch(
          `${API_BASE_URL}/admin/postulaciones/${postulacionId}/ver-adjunto`,
          {
            headers: {
              'X-Timestamp': timestamp,
              'X-Signature': signatureView,
              'X-API-Key': process.env.REACT_APP_API_KEY,
            },
          }
        );

        if (!viewResponse.ok) {
          throw new Error('Error al obtener el PDF');
        }

        const pdfBlob = await viewResponse.blob();
        const pdfUrl = URL.createObjectURL(pdfBlob);

        setSelectedPdf({
          id: postulacionId,
          nombre: nombreArchivo,
          url: pdfUrl,
          blob: pdfBlob, // Guardamos el blob para limpiar después
        });
        setModalVisible(true);
      } else {
        showErrorModalMessage('Archivo no encontrado');
      }
    } catch (error) {
      console.error('Error verificando adjunto:', error);
      showErrorModalMessage('Error al verificar el archivo');
    }
  };

  // Función para descargar PDF
  const descargarPDF = async (postulacionId, nombreArchivo) => {
    try {
      const token = localStorage.getItem('admin_token');
      const timestamp = Math.floor(Date.now() / 1000).toString();

      // Generar firma para ver el PDF (usamos ver-adjunto en lugar de adjunto)
      const signature = await generateSignature(
        process.env.REACT_APP_API_SECRET,
        'GET',
        `/admin/postulaciones/${postulacionId}/ver-adjunto`,
        timestamp
      );

      const response = await fetch(
        `${API_BASE_URL}/admin/postulaciones/${postulacionId}/ver-adjunto`,
        {
          headers: {
            'X-Timestamp': timestamp,
            'X-Signature': signature,
            'X-API-Key': process.env.REACT_APP_API_KEY,
          },
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          showErrorModalMessage('Archivo no encontrado');
        } else {
          throw new Error('Error descargando archivo');
        }
        return;
      }

      const blob = await response.blob();
      const fileUrl = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = fileUrl;
      a.download = nombreArchivo || `postulacion_${postulacionId}.pdf`;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      window.URL.revokeObjectURL(fileUrl);

      showNotification('Descarga iniciada', 'success');
    } catch (error) {
      console.error('Error descargando PDF:', error);
      showErrorModalMessage('Error al descargar el archivo');
    }
  };

  // Funciones para manejar vacantes
  const handleNuevaVacante = () => {
    setEditingVacante(null);
    setVacanteForm({
      status: 'activo',
      idioma_vacante: 'Español',
      slug: '',
      titulo_vacante: '',
      descripcion_corta: '',
      grado_estudios: '',
      responsabilidades: '',
      area_departamento: '',
      nombre_empresa: '',
      calle: '',
      localidad: '',
      estado: '',
      codigo_postal: '',
      pais: 'MX',
      moneda: 'MXN',
      salario: '',
      tipo_salario: 'Mensual',
      perfil: '',
      actividades: '',
      ofrecemos: '',
      lugar_trabajo: '',
      msj_importante: '',
      fecha_posteo: new Date().toISOString().split('T')[0],
      tipo_empleo: 'Tiempo completo',
      horario: '',
      info_empresa: '',
      web_empresa: '',
      correo_empresa: '',
      finaliza_vacante: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split('T')[0],
      url_ubicacion: '',
    });
    setShowVacanteModal(true);
  };

  const handleEditarVacante = (vacante) => {
    setEditingVacante(vacante);
    setVacanteForm({
      ...vacante,
      fecha_posteo: vacante.fecha_posteo
        ? vacante.fecha_posteo.split('T')[0]
        : new Date().toISOString().split('T')[0],
      finaliza_vacante: vacante.finaliza_vacante
        ? vacante.finaliza_vacante.split('T')[0]
        : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
            .toISOString()
            .split('T')[0],
    });
    setShowVacanteModal(true);
  };

  const handleSubmitVacante = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const method = editingVacante ? 'PUT' : 'POST';
      const url = editingVacante
        ? `${API_BASE_URL}/admin/vacantes/${editingVacante.id}`
        : `${API_BASE_URL}/admin/vacantes`;

      // Generar timestamp y firma
      const timestamp = Math.floor(Date.now() / 1000).toString();

      const signature = await generateSignature(
        process.env.REACT_APP_API_SECRET,
        method,
        editingVacante
          ? `/admin/vacantes/${editingVacante.id}`
          : `/admin/vacantes`,
        timestamp
      );

      // Preparar datos a enviar
      let dataToSend = {
        ...vacanteForm,
        pais: 'MX',
        moneda: 'MXN',
        nombre_empresa: 'Packsys',
        info_empresa: 'Packsys Fabricación de envases y embalajes',
        web_empresa: 'https://packsys.com/',
        correo_empresa: 'unete@packsys.com',
      };

      // Si estamos editando y el título NO ha cambiado, NO enviar el slug
      if (
        editingVacante &&
        vacanteForm.titulo_vacante === editingVacante.titulo_vacante
      ) {
        // Eliminar el slug del objeto a enviar
        const { slug, ...dataWithoutSlug } = dataToSend;
        dataToSend = dataWithoutSlug;
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Timestamp': timestamp,
          'X-Signature': signature,
          'X-API-Key': process.env.REACT_APP_API_KEY,
        },
        body: JSON.stringify(dataToSend),
      });

      if (response.ok) {
        fetchVacantes();
        setShowVacanteModal(false);
        showNotification(
          editingVacante
            ? 'Vacante actualizada correctamente'
            : 'Vacante creada correctamente',
          'success'
        );
      } else {
        // Capturar errores de la API
        const errorData = await response.json();
        if (response.status === 400 && errorData.detail?.includes('slug')) {
          showNotification(
            'Error: El slug ya existe. Cambia el título.',
            'error'
          );
        } else {
          throw new Error('Error al guardar la vacante');
        }
      }
    } catch (error) {
      console.error('Error saving vacante:', error);
      showNotification('Error al guardar la vacante', 'error');
    }
  };

  // Funciones de utilidad
  const showNotification = (message, type = 'info') => {
    // Implementar notificación bonita (puedes usar Toast de librería o custom)
    alert(`${type === 'error' ? '❌' : '✅'} ${message}`);
  };

  const formatFecha = (fecha) => {
    if (!fecha) return 'N/A';
    const date = new Date(fecha);
    return date.toLocaleDateString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  // Render modal de confirmación
  const renderConfirmModal = () => {
    if (!showConfirmModal) return null;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          {/* Fondo desenfocado */}
          <div
            className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 backdrop-blur-sm"
            onClick={() => setShowConfirmModal(false)}
          ></div>

          {/* Modal */}
          <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {confirmModalData.title}
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      {confirmModalData.message}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                onClick={() => {
                  confirmModalData.onConfirm();
                  setShowConfirmModal(false);
                }}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Eliminar
              </button>
              <button
                type="button"
                onClick={() => setShowConfirmModal(false)}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render modal de error
  const renderErrorModal = () => {
    if (!showErrorModal) return null;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          {/* Fondo desenfocado */}
          <div
            className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 backdrop-blur-sm"
            onClick={() => setShowErrorModal(false)}
          ></div>

          {/* Modal */}
          <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <ExclamationCircleIcon className="h-6 w-6 text-red-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Error
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">{errorModalMessage}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                onClick={() => setShowErrorModal(false)}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Eliminamos las variables de paginación para postulaciones
  // Ahora mostramos todas las postulaciones directamente
  const currentPosts = postulaciones; // Ya no hay paginación

  const filteredVacantes = (Array.isArray(vacantes) ? vacantes : [])
    .filter((vacante) => {
      if (filtroStatus && vacante.status !== filtroStatus) return false;
      if (filtroArea && vacante.area_departamento !== filtroArea) return false;
      return true;
    })
    .sort((a, b) => {
      if (filtroOrden === 'postulaciones') {
        return (b.total_postulaciones || 0) - (a.total_postulaciones || 0);
      }
      if (filtroOrden === 'fecha') {
        return new Date(b.fecha_posteo) - new Date(a.fecha_posteo);
      }
      return 0;
    });

  // Eliminamos las variables de paginación para vacantes
  // Ahora mostramos todas las vacantes filtradas directamente
  const currentVacantes = filteredVacantes; // Ya no hay paginación

  // Modifica tu tabla para incluir la columna de PDF
  const renderPostulacionesTable = () => {
    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                ID
              </th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                Nombre
              </th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                Fecha
              </th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                Teléfono
              </th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                Vacante
              </th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                Área
              </th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                Adjunto
              </th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody>
            {currentPosts.length > 0 ? (
              currentPosts.map((post) => (
                <tr
                  key={post.id}
                  className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                >
                  <td className="py-3 px-4">
                    <div className="font-mono text-gray-600">#{post.id}</div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="font-medium text-gray-900">
                      {post.nombre} {post.apellidos}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="text-gray-600">
                      {formatFecha(post.fecha_creacion) || 'No especificada'}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="text-gray-600">
                      {post.telefono || 'No especificado'}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full truncate max-w-[200px]">
                      {post.titulovacante}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="inline-block px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
                      {post.areainteres || 'General'}
                    </span>
                  </td>

                  {/* Columna para adjunto */}
                  <td className="py-3 px-4">
                    {post.adjunto ? (
                      <div className="flex items-center space-x-2">
                        <DocumentTextIcon className="h-5 w-5 text-green-600" />
                        <span className="text-sm text-gray-600 truncate max-w-[120px]">
                          {post.adjunto.split('/').pop()}
                        </span>
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">Sin adjunto</span>
                    )}
                  </td>

                  {/* Acciones con botones para PDF */}
                  <td className="py-3 px-4">
                    <div className="flex items-center space-x-2">
                      {/* Botón combinado para ver detalles/PDF */}
                      <button
                        onClick={() => {
                          if (post.adjunto) {
                            abrirPDFModal(post.id, post.adjunto);
                          } else {
                            showErrorModalMessage('Archivo no encontrado');
                          }
                        }}
                        className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
                        title={
                          post.adjunto ? 'Ver PDF y detalles' : 'Sin adjunto'
                        }
                      >
                        <EyeIcon className="h-4 w-4 text-blue-600" />
                      </button>

                      {/* Botón para descargar PDF */}
                      <button
                        onClick={() => descargarPDF(post.id, post.adjunto)}
                        className="p-2 hover:bg-orange-50 rounded-lg transition-colors"
                        title="Descargar PDF"
                      >
                        <ArrowDownTrayIcon className="h-4 w-4 text-orange-600" />
                      </button>

                      {/* Botón para eliminar */}
                      <button
                        onClick={() => showDeleteConfirmationModal(post.id)}
                        className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                        title="Eliminar"
                      >
                        <TrashIcon className="h-4 w-4 text-red-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="py-8 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <FunnelIcon className="h-16 w-16 text-gray-300 mb-4" />
                    <h4 className="text-lg font-medium text-gray-900 mb-2">
                      No hay postulaciones
                    </h4>
                    <p className="text-gray-500">
                      No se encontraron postulaciones con los filtros aplicados.
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Eliminamos la paginación para postulaciones */}
      </div>
    );
  };

  // Modal para ver PDF
  const renderPDFModal = () => {
    if (!modalVisible || !selectedPdf) return null;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          {/* Fondo oscuro desenfocado */}
          <div
            className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 backdrop-blur-sm"
            onClick={() => {
              // Limpiar el blob URL al cerrar
              if (selectedPdf?.url && selectedPdf.url.startsWith('blob:')) {
                URL.revokeObjectURL(selectedPdf.url);
              }
              setModalVisible(false);
            }}
          ></div>

          {/* Modal */}
          <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  PDF: {selectedPdf.nombre}
                </h3>
                <button
                  onClick={() => {
                    // Limpiar el blob URL al cerrar
                    if (
                      selectedPdf?.url &&
                      selectedPdf.url.startsWith('blob:')
                    ) {
                      URL.revokeObjectURL(selectedPdf.url);
                    }
                    setModalVisible(false);
                  }}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Contenedor del PDF */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <iframe
                  src={selectedPdf.url}
                  title={`PDF de postulación ${selectedPdf.id}`}
                  width="100%"
                  height="600px"
                  style={{ border: 'none' }}
                />
              </div>

              <div className="mt-4 flex justify-end space-x-3">
                <button
                  onClick={() =>
                    descargarPDF(selectedPdf.id, selectedPdf.nombre)
                  }
                  className="px-4 py-2 bg-primary-blue text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  <span>Descargar PDF</span>
                </button>
                <button
                  onClick={() => {
                    if (
                      selectedPdf?.url &&
                      selectedPdf.url.startsWith('blob:')
                    ) {
                      URL.revokeObjectURL(selectedPdf.url);
                    }
                    setModalVisible(false);
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Modal para nueva/edición de vacante
  const renderVacanteModal = () => {
    if (!showVacanteModal) return null;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div
            className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 backdrop-blur-sm"
            onClick={() => setShowVacanteModal(false)}
          ></div>

          <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900">
                  {editingVacante ? 'Editar Vacante' : 'Nueva Vacante'}
                </h3>
                <button
                  onClick={() => setShowVacanteModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Título de la Vacante *
                  </label>
                  <input
                    type="text"
                    value={vacanteForm.titulo_vacante}
                    onChange={(e) =>
                      setVacanteForm({
                        ...vacanteForm,
                        titulo_vacante: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Slug *
                  </label>
                  <input
                    type="text"
                    value={vacanteForm.slug}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                    required
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Generado automáticamente a partir del título
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status *
                  </label>
                  <select
                    value={vacanteForm.status}
                    onChange={(e) =>
                      setVacanteForm({ ...vacanteForm, status: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                  >
                    <option value="activo">Activo</option>
                    <option value="inactivo">Inactivo</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Descripción Corta *
                  </label>
                  <textarea
                    value={vacanteForm.descripcion_corta}
                    onChange={(e) =>
                      setVacanteForm({
                        ...vacanteForm,
                        descripcion_corta: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                    rows="3"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Grado de Estudios *
                  </label>
                  <textarea
                    value={vacanteForm.grado_estudios}
                    onChange={(e) =>
                      setVacanteForm({
                        ...vacanteForm,
                        grado_estudios: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                    rows="3"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Responsabilidades *
                  </label>
                  <textarea
                    value={vacanteForm.responsabilidades}
                    onChange={(e) =>
                      setVacanteForm({
                        ...vacanteForm,
                        responsabilidades: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                    rows="3"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Idioma Vacante *
                  </label>
                  <input
                    type="text"
                    value={vacanteForm.idioma_vacante}
                    onChange={(e) =>
                      setVacanteForm({
                        ...vacanteForm,
                        idioma_vacante: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Área/Departamento *
                  </label>
                  <input
                    type="text"
                    value={vacanteForm.area_departamento}
                    onChange={(e) =>
                      setVacanteForm({
                        ...vacanteForm,
                        area_departamento: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de Empleo *
                  </label>
                  <select
                    value={vacanteForm.tipo_empleo}
                    onChange={(e) =>
                      setVacanteForm({
                        ...vacanteForm,
                        tipo_empleo: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                  >
                    <option value="Tiempo completo">Tiempo completo</option>
                    <option value="Medio tiempo">Medio tiempo</option>
                    <option value="Por proyecto">Por proyecto</option>
                    <option value="Temporal">Temporal</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Calle *
                  </label>
                  <input
                    type="text"
                    value={vacanteForm.calle}
                    onChange={(e) =>
                      setVacanteForm({
                        ...vacanteForm,
                        calle: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Localidad *
                  </label>
                  <input
                    type="text"
                    value={vacanteForm.localidad}
                    onChange={(e) =>
                      setVacanteForm({
                        ...vacanteForm,
                        localidad: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Código Postal *
                  </label>
                  <input
                    type="text"
                    value={vacanteForm.codigo_postal}
                    onChange={(e) =>
                      setVacanteForm({
                        ...vacanteForm,
                        codigo_postal: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estado *
                  </label>
                  <input
                    type="text"
                    value={vacanteForm.estado}
                    onChange={(e) =>
                      setVacanteForm({ ...vacanteForm, estado: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Salario *
                  </label>
                  <input
                    type="text"
                    value={vacanteForm.salario}
                    onChange={(e) =>
                      setVacanteForm({
                        ...vacanteForm,
                        salario: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de Salario *
                  </label>
                  <select
                    value={vacanteForm.tipo_salario}
                    onChange={(e) =>
                      setVacanteForm({
                        ...vacanteForm,
                        tipo_salario: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                    required
                  >
                    <option value="Mensual">Mensual</option>
                    <option value="Quincenal">Quincenal</option>
                    <option value="Semanal">Semanal</option>
                    <option value="Por hora">Por hora</option>
                  </select>
                </div>

                <div className="md:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Perfil Requerido *
                  </label>
                  <textarea
                    value={vacanteForm.perfil}
                    onChange={(e) =>
                      setVacanteForm({ ...vacanteForm, perfil: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                    rows="3"
                    required
                  />
                </div>

                <div className="md:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Actividades *
                  </label>
                  <textarea
                    value={vacanteForm.actividades}
                    onChange={(e) =>
                      setVacanteForm({
                        ...vacanteForm,
                        actividades: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                    rows="3"
                    required
                  />
                </div>

                <div className="md:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Lo que Ofrecemos *
                  </label>
                  <textarea
                    value={vacanteForm.ofrecemos}
                    onChange={(e) =>
                      setVacanteForm({
                        ...vacanteForm,
                        ofrecemos: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                    rows="3"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Horario *
                  </label>
                  <input
                    type="text"
                    value={vacanteForm.horario}
                    onChange={(e) =>
                      setVacanteForm({
                        ...vacanteForm,
                        horario: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fecha de Finalización *
                  </label>
                  <input
                    type="date"
                    value={vacanteForm.finaliza_vacante}
                    onChange={(e) =>
                      setVacanteForm({
                        ...vacanteForm,
                        finaliza_vacante: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Correo de Contacto *
                  </label>
                  <input
                    type="email"
                    value={vacanteForm.correo_empresa}
                    onChange={(e) =>
                      setVacanteForm({
                        ...vacanteForm,
                        correo_empresa: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                    required
                  />
                </div>

                <div className="md:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Lugar de Trabajo *
                  </label>
                  <input
                    type="text"
                    value={vacanteForm.lugar_trabajo}
                    onChange={(e) =>
                      setVacanteForm({
                        ...vacanteForm,
                        lugar_trabajo: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    URL de Ubicación *
                  </label>
                  <input
                    type="text"
                    value={vacanteForm.url_ubicacion}
                    onChange={(e) =>
                      setVacanteForm({
                        ...vacanteForm,
                        url_ubicacion: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mensaje Importante *
                  </label>
                  <input
                    type="text"
                    value={vacanteForm.msj_importante}
                    onChange={(e) =>
                      setVacanteForm({
                        ...vacanteForm,
                        msj_importante: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                    required
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowVacanteModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSubmitVacante}
                  className="px-6 py-2 bg-gradient-to-r from-gray-700 via-rose-500 to-orange-400 text-white font-medium rounded-lg hover:shadow-lg transition-all duration-300"
                >
                  {editingVacante ? 'Actualizar Vacante' : 'Crear Vacante'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative inline-flex">
            <div className="w-20 h-20 border-4 border-gray-200 rounded-full"></div>
            <div className="w-20 h-20 border-4 border-primary-orange border-t-transparent rounded-full absolute top-0 left-0 animate-spin"></div>
          </div>
          <p className="mt-6 text-gray-600 font-medium">
            Cargando panel de administración...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-800 via-blue-700 to-gray-900 text-white shadow-lg">
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div className="py-4">
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
                Panel de Administración
              </h1>
              <p className="text-white/80">
                Gestiona postulaciones y vacantes del sistema
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="hidden md:flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>
                <span className="font-medium">
                  {localStorage.getItem('admin_name') || 'Administrador'}
                </span>
              </div>
              <button
                onClick={() => {
                  localStorage.removeItem('admin_token');
                  localStorage.removeItem('admin_email');
                  window.location.href = '/';
                }}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white font-medium rounded-lg transition-colors"
              >
                Cerrar sesión
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="container mx-auto px-4 -mt-4 relative z-10">
        <div className="flex space-x-2 bg-white rounded-lg shadow-md p-2">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`flex-1 py-3 px-4 rounded-md font-medium transition-all ${
              activeTab === 'dashboard'
                ? 'bg-gradient-to-r from-gray-700 via-rose-500 to-orange-400 text-white shadow-md'
                : 'text-gray-600 hover:text-primary-blue hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-center space-x-2">
              <ChartBarIcon className="h-5 w-5" />
              <span>Dashboard</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('postulaciones')}
            className={`flex-1 py-3 px-4 rounded-md font-medium transition-all ${
              activeTab === 'postulaciones'
                ? 'bg-gradient-to-r from-gray-700 via-rose-500 to-orange-400 text-white shadow-md'
                : 'text-gray-600 hover:text-primary-blue hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-center space-x-2">
              <EnvelopeIcon className="h-5 w-5" />
              <span>Postulaciones</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('vacantes')}
            className={`flex-1 py-3 px-4 rounded-md font-medium transition-all ${
              activeTab === 'vacantes'
                ? 'bg-gradient-to-r from-gray-700 via-rose-500 to-orange-400 text-white shadow-md'
                : 'text-gray-600 hover:text-primary-blue hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-center space-x-2">
              <BriefcaseIcon className="h-5 w-5" />
              <span>Vacantes</span>
            </div>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {activeTab === 'dashboard' && (
          <div className="space-y-8">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Postulaciones</p>
                    <p className="text-3xl font-bold text-gray-900 mt-2">
                      {stats.total_postulaciones}
                    </p>
                  </div>
                  <div className="p-3 bg-blue-100 rounded-xl">
                    <UserGroupIcon className="h-8 w-8 text-blue-600" />
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center text-sm text-gray-500">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: '100%' }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Postulaciones Hoy</p>
                    <p className="text-3xl font-bold text-gray-900 mt-2">
                      {stats.postulaciones_hoy}
                    </p>
                  </div>
                  <div className="p-3 bg-green-100 rounded-xl">
                    <CalendarDaysIcon className="h-8 w-8 text-green-600" />
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center text-sm text-gray-500">
                    <span className="text-green-600 font-medium">↑ Hoy</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Vacantes Activas</p>
                    <p className="text-3xl font-bold text-gray-900 mt-2">
                      {stats.vacantes_activas}
                    </p>
                  </div>
                  <div className="p-3 bg-orange-100 rounded-xl">
                    <BriefcaseIcon className="h-8 w-8 text-orange-600" />
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center text-sm text-gray-500">
                    <span className="text-orange-600 font-medium">
                      Disponibles
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Post. Este Mes</p>
                    <p className="text-3xl font-bold text-gray-900 mt-2">
                      {stats.postulaciones_este_mes}
                    </p>
                  </div>
                  <div className="p-3 bg-purple-100 rounded-xl">
                    <ChartBarIcon className="h-8 w-8 text-purple-600" />
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center text-sm text-gray-500">
                    <span className="text-purple-600 font-medium">
                      ↑ Este mes
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Áreas Populares */}
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-gray-900">
                    Áreas Más Populares
                  </h3>
                  <span className="text-sm text-gray-500">
                    Por postulaciones
                  </span>
                </div>
                <div className="space-y-4">
                  {stats.areas_populares.map((area, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center space-x-3">
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm"
                          style={{
                            backgroundColor:
                              index === 0
                                ? '#3B82F6'
                                : index === 1
                                  ? '#10B981'
                                  : index === 2
                                    ? '#F59E0B'
                                    : index === 3
                                      ? '#8B5CF6'
                                      : '#EF4444',
                          }}
                        >
                          {index + 1}
                        </div>
                        <span className="font-medium text-gray-700">
                          {area.area}
                        </span>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="w-32 bg-gray-200 rounded-full h-3">
                          <div
                            className="h-3 rounded-full"
                            style={{
                              width: `${Math.min(
                                100,
                                (area.cantidad /
                                  Math.max(
                                    1,
                                    stats.areas_populares[0]?.cantidad || 1
                                  )) *
                                  100
                              )}%`,
                              backgroundColor:
                                index === 0
                                  ? '#3B82F6'
                                  : index === 1
                                    ? '#10B981'
                                    : index === 2
                                      ? '#F59E0B'
                                      : index === 3
                                        ? '#8B5CF6'
                                        : '#EF4444',
                            }}
                          ></div>
                        </div>
                        <span className="font-bold text-gray-900">
                          {area.cantidad}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Vacantes Populares */}
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-gray-900">
                    Vacantes Más Solicitadas
                  </h3>
                  <span className="text-sm text-gray-500">Top 5</span>
                </div>
                <div className="space-y-4">
                  {stats.vacantes_populares.map((vacante, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 truncate">
                          {vacante.vacante}
                        </p>
                        <p className="text-sm text-gray-500">
                          {vacante.cantidad} postulaciones
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                        <span className="text-sm font-medium text-gray-700">
                          Activa
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Últimas Postulaciones */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">
                  Últimas Postulaciones
                </h3>
                <button
                  onClick={() => setActiveTab('postulaciones')}
                  className="text-primary-blue hover:text-primary-orange font-medium"
                >
                  Ver todas →
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                        Nombre
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                        Email
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                        Vacante
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                        Fecha
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.ultimas_postulaciones?.map((post) => (
                      <tr
                        key={post.id}
                        className="border-b border-gray-100 hover:bg-gray-50"
                      >
                        <td className="py-3 px-4">
                          <div className="font-medium text-gray-900">
                            {post.nombre} {post.apellidos}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-gray-600">
                          {post.email || 'No disponible'}
                        </td>
                        <td className="py-3 px-4">
                          <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full truncate max-w-[250px]">
                            {post.vacante}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-gray-500">
                          {formatFecha(post.fecha)}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => {
                                if (post.adjunto) {
                                  abrirPDFModal(post.id, post.adjunto);
                                } else {
                                  showErrorModalMessage(
                                    'Archivo no encontrado'
                                  );
                                }
                              }}
                              className="p-1 hover:bg-blue-50 rounded"
                              title="Ver PDF"
                            >
                              <EyeIcon className="h-4 w-4 text-blue-600" />
                            </button>
                            <button
                              onClick={() =>
                                showDeleteConfirmationModal(post.id)
                              }
                              className="p-1 hover:bg-red-50 rounded"
                              title="Eliminar"
                            >
                              <TrashIcon className="h-4 w-4 text-red-600" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'postulaciones' && (
          <div className="space-y-8">
            {/* Filters Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar por nombre..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent transition-all"
                  />
                </div>

                <select
                  value={selectedArea}
                  onChange={(e) => setSelectedArea(e.target.value)}
                  className="px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent transition-all bg-white"
                >
                  <option value="">Todas las áreas</option>
                  {stats.areas_populares.map((area) => (
                    <option key={area.area} value={area.area}>
                      {area.area}
                    </option>
                  ))}
                </select>

                <select
                  value={selectedVacante}
                  onChange={(e) => setSelectedVacante(e.target.value)}
                  className="px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent transition-all bg-white"
                >
                  <option value="">Todas las vacantes</option>
                  {vacantes.map((vacante) => (
                    <option key={vacante.id} value={vacante.id}>
                      {vacante.titulo_vacante}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                <div></div>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => handleExport('csv')}
                    className="px-4 py-2 border border-green-600 text-green-600 font-medium rounded-lg hover:bg-green-50 transition-colors flex items-center space-x-2"
                  >
                    <ArrowDownTrayIcon className="h-5 w-5" />
                    <span>Exportar CSV</span>
                  </button>
                  <button
                    onClick={() => handleExport('json')}
                    className="px-4 py-2 border border-blue-600 text-blue-600 font-medium rounded-lg hover:bg-blue-50 transition-colors flex items-center space-x-2"
                  >
                    <ArrowDownTrayIcon className="h-5 w-5" />
                    <span>Exportar JSON</span>
                  </button>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">
                  Postulaciones
                </h3>
                <span className="text-sm text-gray-500">
                  {postulaciones.length}{' '}
                  {postulaciones.length === 1 ? 'resultado' : 'resultados'}
                </span>
              </div>

              {renderPostulacionesTable()}
            </div>
          </div>
        )}

        {/* Render de modales */}
        {renderPDFModal()}
        {renderVacanteModal()}
        {renderConfirmModal()}
        {renderErrorModal()}

        {activeTab === 'vacantes' && (
          <div className="space-y-8">
            {/* Vacantes Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-2xl p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm opacity-90">Vacantes Totales</p>
                    <p className="text-3xl font-bold mt-2">
                      {
                        vacantes.filter(
                          (vacante) => vacante.status !== 'desactivado'
                        ).length
                      }
                    </p>
                  </div>
                  <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                    <BriefcaseIcon className="h-8 w-8" />
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-2xl p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm opacity-90">Vacantes Activas</p>
                    <p className="text-3xl font-bold mt-2">
                      {vacantes.filter((v) => v.status === 'activo').length}
                    </p>
                  </div>
                  <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                    <ChartBarIcon className="h-8 w-8" />
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-2xl p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm opacity-90">Vacantes Inactivas</p>
                    <p className="text-3xl font-bold mt-2">
                      {vacantes.filter((v) => v.status === 'inactivo').length}
                    </p>
                  </div>
                  <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                    <ExclamationTriangleIcon className="h-8 w-8" />
                  </div>
                </div>
              </div>
            </div>

            {/* Filtros para vacantes */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <select
                  value={filtroStatus}
                  onChange={(e) => setFiltroStatus(e.target.value)}
                  className="px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent transition-all bg-white"
                >
                  <option value="">Todos los estados</option>
                  <option value="activo">Activo</option>
                  <option value="inactivo">Inactivo</option>
                </select>

                <select
                  value={filtroArea}
                  onChange={(e) => setFiltroArea(e.target.value)}
                  className="px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent transition-all bg-white"
                >
                  <option value="">Todas las áreas</option>
                  {[...new Set(vacantes.map((v) => v.area_departamento))].map(
                    (area) => (
                      <option key={area} value={area}>
                        {area}
                      </option>
                    )
                  )}
                </select>

                <select
                  value={filtroOrden}
                  onChange={(e) => setFiltroOrden(e.target.value)}
                  className="px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent transition-all bg-white"
                >
                  <option value="">Ordenar por</option>
                  <option value="postulaciones">Más postulaciones</option>
                  <option value="fecha">Más recientes</option>
                </select>
              </div>
            </div>

            {/* Vacantes List */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    Vacantes Disponibles
                  </h3>
                  <p className="text-gray-500 text-sm mt-1">
                    Gestiona todas las vacantes del sistema
                  </p>
                </div>
                <button
                  onClick={handleNuevaVacante}
                  className="px-6 py-3 bg-gradient-to-r from-gray-700 via-rose-500 to-orange-400 text-white font-medium rounded-lg hover:shadow-lg transition-all duration-300 flex items-center space-x-2"
                >
                  <PlusIcon className="h-5 w-5" />
                  <span>Nueva Vacante</span>
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                        ID
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                        Título
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                        Área
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                        Ubicación
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                        Estado
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                        Postulaciones
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentVacantes
                      .filter((vacante) => vacante.status !== 'desactivado')
                      .map((vacante) => (
                        <tr
                          key={vacante.id}
                          className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                        >
                          <td className="py-3 px-4">
                            <div className="font-mono text-gray-600">
                              #{vacante.id}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="font-medium text-gray-900">
                              {vacante.titulo_vacante}
                            </div>
                            <div className="text-sm text-gray-500 mt-1 truncate max-w-xs">
                              {vacante.descripcion_corta}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                              {vacante.area_departamento}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="text-gray-600">
                              {vacante.localidad}, {vacante.estado}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span
                              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                                vacante.status === 'activo'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {vacante.status === 'activo'
                                ? 'Activo'
                                : 'Inactivo'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="text-center">
                              <span className="font-medium text-gray-900">
                                {vacante.total_postulaciones || 0}
                              </span>
                              <div className="text-xs text-gray-500">
                                postulaciones
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() =>
                                  window.open(
                                    `/vacante/${vacante.slug}`,
                                    '_blank'
                                  )
                                }
                                className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Ver vacante"
                              >
                                <EyeIcon className="h-4 w-4 text-blue-600" />
                              </button>
                              <button
                                onClick={() => handleEditarVacante(vacante)}
                                className="p-2 hover:bg-green-50 rounded-lg transition-colors"
                                title="Editar vacante"
                              >
                                <PencilIcon className="h-4 w-4 text-green-600" />
                              </button>
                              <button
                                onClick={() =>
                                  showDeleteVacanteConfirmationModal(vacante.id)
                                }
                                className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                                title="Eliminar vacante"
                              >
                                <TrashIcon className="h-4 w-4 text-red-600" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>

                {/* Eliminamos la paginación para vacantes */}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
